// Matches a Danish CPR (ddMMyy-XXXX) or any valid prefix of one, so partial
// input is recognised as the user types. Day (01-31) and month (01-12) are
// range-checked even on partial input; the two year digits are not. The dash is
// optional and, if present, may appear only once and only after the 6 date
// digits, followed by up to 4 serial digits (at most 10 digits in total).
const CPR_PREFIX_PATTERN =
  /^(?:[0-3]|(?:0[1-9]|[12]\d|3[01])(?:[01]|(?:0[1-9]|1[0-2])(?:\d|\d{2}(?:-?\d{0,4})?)?)?)$/;

export function isCprPrefix(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length > 0 && CPR_PREFIX_PATTERN.test(trimmed);
}

export function stripNonDigits(value: string): string {
  return value.replace(/\D/g, '');
}

// Converts a 1-based index to spreadsheet-style column letters: 1 -> "A",
// 26 -> "Z", 27 -> "AA", 28 -> "AB". Bijective base-26, so it never repeats and
// never runs out. Returns "" for values below 1.
export function toColumnLetters(index: number): string {
  let result = '';
  let remaining = Math.floor(index);
  while (remaining > 0) {
    const remainder = (remaining - 1) % 26;
    result = String.fromCodePoint(65 + remainder) + result;
    remaining = Math.floor((remaining - 1) / 26);
  }
  return result;
}

// Up to two uppercase initials for a monogram: first letters of the first two
// words, or the first two characters when there is only one word.
export function getInitials(value: string): string {
  const words = value.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

// True when `name` already appears in `takenNames`, comparing trimmed and
// case-insensitively so "Odense " and "odense" count as the same value.
// Lowercasing is locale-independent on purpose: the same two names must compare
// equal wherever the app runs, and the Danish letters (æ ø å) fold identically
// under both the invariant and the Danish mapping.
export function isNameTaken(name: string, takenNames: string[]): boolean {
  const normalized = name.trim().toLowerCase();
  return takenNames.some((taken) => taken.trim().toLowerCase() === normalized);
}

// Stable, deterministic bucket in [0, buckets) for a string. Used to give each
// value a consistent colour from a fixed palette across renders. Uses a djb2
// hash (seed 5381, multiplier 33) so similar strings (e.g. "Sano Aarhus" vs
// "Sano Odense") still spread across buckets instead of colliding. The
// accumulator is reduced modulo a large prime (2^31 - 1) each step, which keeps
// it a bounded, non-negative safe integer without relying on 32-bit bitwise
// overflow. Iterates by code point so surrogate pairs hash as a whole.
export function hashStringToIndex(value: string, buckets: number): number {
  if (buckets <= 0) return 0;
  let hash = 5381;
  for (const char of value) {
    hash = (hash * 33 + (char.codePointAt(0) ?? 0)) % 2147483647;
  }
  return hash % buckets;
}

// Removes angle-bracket markup from a string. Translation copy carries `<bold>`
// markers that <Trans> turns into elements; anywhere the plain string is used
// instead (a screen-reader-only announcement, say) the markers would be read out
// literally.
// The character class excludes `<` as well as `>` so a malformed run like `<a <b>`
// cannot be scanned repeatedly: every match attempt ends at the next angle bracket.
export function stripTags(value: string): string {
  return value.replace(/<\/?[^<>]+>/g, '');
}
